/**
 * Custom hook for managing stimulus-based focus group sessions.
 * Pattern follows useCompare.js.
 */
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from '../i18n/LanguageContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function useStimulus() {
  const { t, language } = useTranslation();
  const [stimulusStage, setStimulusStage] = useState('setup'); // setup | running | guidance | report
  const [stimulusText, setStimulusText] = useState('');
  const [stimulusLabel, setStimulusLabel] = useState('');
  const [questions, setQuestions] = useState(['']);

  // Session data
  const [messages, setMessages] = useState([]);
  const [roundSummaries, setRoundSummaries] = useState([]);
  const [moderatorSummary, setModeratorSummary] = useState('');
  const [finalAnalysis, setFinalAnalysis] = useState(null);
  const [beliefStates, setBeliefStates] = useState({});
  const [sessionId, setSessionId] = useState(null);

  const [currentRound, setCurrentRound] = useState(0);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const streamingContentRef = useRef('');
  const currentSpeakerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // --- Question management ---
  const updateQuestion = useCallback((idx, val) => {
    setQuestions(prev => {
      const updated = [...prev];
      updated[idx] = val;
      return updated;
    });
  }, []);

  const addQuestion = useCallback(() => {
    setQuestions(prev => [...prev, '']);
  }, []);

  const removeQuestion = useCallback((idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // --- Stream event handler ---
  const handleStreamEvent = useCallback((event) => {
    const type = event.type;

    if (type === 'session_start') {
      setSessionId(event.session_id);
    } else if (type === 'round_start') {
      setCurrentRound(event.round);
    } else if (type === 'speaker_start') {
      const speaker = {
        name: event.speaker,
        id: event.speaker_id,
        round: event.round,
        model: event.model,
      };
      currentSpeakerRef.current = speaker;
      streamingContentRef.current = '';
      setCurrentSpeaker(speaker);
      setStreamingContent('');
    } else if (type === 'content_chunk') {
      streamingContentRef.current += event.chunk;
      setStreamingContent(streamingContentRef.current);
    } else if (type === 'speaker_end') {
      const finalContent = streamingContentRef.current;
      const speakerInfo = currentSpeakerRef.current;

      if (finalContent && finalContent.trim()) {
        const newMessage = {
          id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          speaker: speakerInfo?.name || event.speaker,
          speakerId: speakerInfo?.id || 'unknown',
          content: finalContent,
          round: speakerInfo?.round || 0,
          model: speakerInfo?.model,
        };
        setMessages(prev => [...prev, newMessage]);
      }

      streamingContentRef.current = '';
      currentSpeakerRef.current = null;
      setStreamingContent('');
      setCurrentSpeaker(null);
    } else if (type === 'message_analyzed') {
      setMessages(prev => {
        if (prev.length === 0) return prev;
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          sentiment: event.sentiment,
          keyPoints: event.key_points,
        };
        return updated;
      });
    } else if (type === 'round_summary') {
      setRoundSummaries(prev => [...prev, event.summary]);
    } else if (type === 'belief_update') {
      setBeliefStates(prev => ({
        ...prev,
        [event.speaker_id]: [...(prev[event.speaker_id] || []), event.belief_state],
      }));
    } else if (type === 'moderator_summary_start') {
      setModeratorSummary('');
    } else if (type === 'moderator_summary_chunk') {
      setModeratorSummary(prev => prev + event.chunk);
    } else if (type === 'moderator_summary_end') {
      // no-op
    } else if (type === 'final_analysis') {
      setFinalAnalysis(event.analysis);
      setIsGeneratingReport(false);
    } else if (type === 'generating_report') {
      setIsGeneratingReport(true);
    } else if (type === 'discussion_complete') {
      setStimulusStage('guidance');
    } else if (type === 'session_complete') {
      setIsGeneratingReport(false);
      setStimulusStage('report');
    } else if (type === 'error') {
      setError(event.message || t('errors.sessionError'));
    }
  }, [t]);

  // --- Start stimulus session ---
  const startStimulus = useCallback(async (config, personas) => {
    if (!stimulusText.trim() || questions.filter(q => q.trim()).length === 0 || personas.length === 0) return;

    setStimulusStage('running');
    setError(null);
    setMessages([]);
    setRoundSummaries([]);
    setModeratorSummary('');
    setFinalAnalysis(null);
    setBeliefStates({});
    setCurrentRound(0);
    setIsGeneratingReport(false);
    streamingContentRef.current = '';
    currentSpeakerRef.current = null;
    setStreamingContent('');
    setCurrentSpeaker(null);

    abortControllerRef.current = new AbortController();

    const validQuestions = questions.filter(q => q.trim());

    try {
      const response = await fetch(`${API_BASE}/session/stimulus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: config.title || '',
          topic: config.topic || '',
          stimulus_text: stimulusText,
          stimulus_label: stimulusLabel,
          questions: validQuestions,
          description: config.description || '',
          moderator_briefing: config.moderatorBriefing || '',
          moderator_direction: config.moderatorDirection || '',
          moderator_avoid: config.moderatorAvoid || '',
          personas: personas,
          moderator_model: config.moderatorModel || 'claude-sonnet-4.6',
          language: language,
          devils_advocate: config.devilsAdvocate || false,
          enhanced_belief_tracking: config.enhancedBeliefTracking || false,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) throw new Error('Failed to start stimulus session');

      const sid = response.headers.get('X-Session-ID');
      if (sid) setSessionId(sid);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const ev = JSON.parse(line.slice(6));
              handleStreamEvent(ev);
            } catch (e) {
              console.error('Failed to parse stimulus event:', e);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(t('errors.sessionError'));
      console.error(err);
      setStimulusStage('setup');
    }
  }, [stimulusText, stimulusLabel, questions, handleStreamEvent, language, t]);

  // --- Load template data ---
  const loadTemplate = useCallback((templateData) => {
    const config = templateData.config || {};
    if (config.stimulus_text) setStimulusText(config.stimulus_text);
    if (config.stimulus_label) setStimulusLabel(config.stimulus_label);
    if (config.questions && config.questions.length > 0) setQuestions(config.questions);
  }, []);

  // --- Generate report (called from guidance stage) ---
  const generateReport = useCallback(async (guidance = '') => {
    if (!sessionId) return;

    setStimulusStage('running');
    setIsGeneratingReport(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/session/${sessionId}/generate-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidance }),
      });

      if (!response.ok) throw new Error('Failed to generate report');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const ev = JSON.parse(line.slice(6));
              handleStreamEvent(ev);
            } catch (e) {
              console.error('Failed to parse event:', e);
            }
          }
        }
      }
    } catch (err) {
      setError(t('errors.reportGenerationError'));
      console.error(err);
      setStimulusStage('guidance');
      setIsGeneratingReport(false);
    }
  }, [sessionId, handleStreamEvent, t]);

  // --- Reset ---
  const resetStimulus = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setStimulusStage('setup');
    setMessages([]);
    setRoundSummaries([]);
    setModeratorSummary('');
    setFinalAnalysis(null);
    setBeliefStates({});
    setCurrentRound(0);
    setCurrentSpeaker(null);
    setStreamingContent('');
    setError(null);
    setSessionId(null);
    setIsGeneratingReport(false);
  }, []);

  return {
    stimulusStage,
    stimulusText,
    stimulusLabel,
    questions,
    messages,
    roundSummaries,
    moderatorSummary,
    finalAnalysis,
    beliefStates,
    sessionId,
    currentRound,
    currentSpeaker,
    streamingContent,
    error,
    isGeneratingReport,
    setError,

    setStimulusText,
    setStimulusLabel,
    setQuestions,
    updateQuestion,
    addQuestion,
    removeQuestion,
    startStimulus,
    generateReport,
    loadTemplate,
    resetStimulus,
  };
}
